/**
 * Form for creating a new credential.
 */

import { useState } from 'react';
import type { CredentialCreateRequest, CredentialType } from '@/types/credential';

interface CredentialFormProps {
  onSubmit: (payload: CredentialCreateRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CREDENTIAL_TYPE_LABELS: Record<CredentialType, string> = {
  ssh_password: 'SSH Password',
  ssh_key: 'SSH Private Key',
  winrm_password: 'WinRM Password',
  snmp: 'SNMP v1/v2c (Community)',
  snmpv3: 'SNMPv3 (Authenticated)',
};

const SNMP_AUTH_PROTOCOLS = ['MD5', 'SHA', 'SHA-256', 'SHA-512'];
const SNMP_PRIV_PROTOCOLS = ['DES', 'AES', 'AES-256'];
const SNMP_SECURITY_LEVELS = [
  { value: 'noAuthNoPriv', label: 'No Auth, No Privacy' },
  { value: 'authNoPriv', label: 'Auth, No Privacy' },
  { value: 'authPriv', label: 'Auth + Privacy (recommended)' },
];

interface FormState {
  name: string;
  credential_type: CredentialType;
  username: string;
  secret: string;
  description: string;
  snmp_auth_protocol: string;
  snmp_auth_password: string;
  snmp_priv_protocol: string;
  snmp_priv_password: string;
  snmp_security_level: string;
}

export default function CredentialForm({ onSubmit, onCancel, isSubmitting }: CredentialFormProps) {
  const [form, setForm] = useState<FormState>({
    name: '',
    credential_type: 'ssh_password',
    username: '',
    secret: '',
    description: '',
    snmp_auth_protocol: 'SHA',
    snmp_auth_password: '',
    snmp_priv_protocol: 'AES',
    snmp_priv_password: '',
    snmp_security_level: 'authPriv',
  });

  function handleChange(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: CredentialCreateRequest = {
      name: form.name,
      credential_type: form.credential_type,
      description: form.description || undefined,
    };

    if (form.credential_type === 'snmp') {
      payload.secret = form.secret; // community string
    } else if (form.credential_type === 'snmpv3') {
      payload.username = form.username;
      payload.snmp_auth_protocol = form.snmp_auth_protocol;
      payload.snmp_auth_password = form.snmp_auth_password;
      payload.snmp_priv_protocol = form.snmp_priv_protocol;
      payload.snmp_priv_password = form.snmp_priv_password;
      payload.snmp_security_level = form.snmp_security_level;
    } else {
      payload.username = form.username;
      payload.secret = form.secret;
    }

    onSubmit(payload);
  }

  const isKeyType = form.credential_type === 'ssh_key';
  const isSnmp = form.credential_type === 'snmp';
  const isSnmpv3 = form.credential_type === 'snmpv3';
  const needsAuth = isSnmpv3 && form.snmp_security_level !== 'noAuthNoPriv';
  const needsPriv = isSnmpv3 && form.snmp_security_level === 'authPriv';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="e.g. Linux prod key"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type <span className="text-red-500">*</span>
        </label>
        <select
          required
          value={form.credential_type}
          onChange={(e) => handleChange('credential_type', e.target.value as CredentialType)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        >
          {(Object.entries(CREDENTIAL_TYPE_LABELS) as [CredentialType, string][]).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>

      {/* ---- SSH / WinRM fields ---- */}
      {!isSnmp && !isSnmpv3 && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="e.g. ubuntu"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isKeyType ? 'Private Key (PEM)' : 'Password'} <span className="text-red-500">*</span>
            </label>
            {isKeyType ? (
              <textarea
                required
                rows={6}
                value={form.secret}
                onChange={(e) => handleChange('secret', e.target.value)}
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:border-blue-500 focus:outline-none"
              />
            ) : (
              <input
                type="password"
                required
                value={form.secret}
                onChange={(e) => handleChange('secret', e.target.value)}
                placeholder="Password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            )}
          </div>
        </>
      )}

      {/* ---- SNMP v1/v2c fields ---- */}
      {isSnmp && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Community String <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            required
            value={form.secret}
            onChange={(e) => handleChange('secret', e.target.value)}
            placeholder="e.g. public"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            The SNMP community string used for v1/v2c authentication.
          </p>
        </div>
      )}

      {/* ---- SNMPv3 fields ---- */}
      {isSnmpv3 && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Security Name (Username) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="e.g. snmpuser"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Security Level <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={form.snmp_security_level}
              onChange={(e) => handleChange('snmp_security_level', e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              {SNMP_SECURITY_LEVELS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {needsAuth && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auth Protocol <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.snmp_auth_protocol}
                  onChange={(e) => handleChange('snmp_auth_protocol', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {SNMP_AUTH_PROTOCOLS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auth Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={form.snmp_auth_password}
                  onChange={(e) => handleChange('snmp_auth_password', e.target.value)}
                  placeholder="Min 8 characters"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </>
          )}

          {needsPriv && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privacy Protocol <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.snmp_priv_protocol}
                  onChange={(e) => handleChange('snmp_priv_protocol', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  {SNMP_PRIV_PROTOCOLS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privacy Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={form.snmp_priv_password}
                  onChange={(e) => handleChange('snmp_priv_password', e.target.value)}
                  placeholder="Min 8 characters"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (optional)
        </label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Where / how is this credential used?"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving…' : 'Save credential'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
